import { Col, Form, Row } from 'antd';
import { get } from 'lodash';
import React, { useCallback, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Trans, withNamespaces } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';

import { signInUser } from '../../api/auth';
import { handleApiCalls, handleApiErrors } from '../../api/axiosInstance';
import { fetchCountryConfig } from '../../api/common';
import { USER_API } from '../../api/user';
import { AddressForm } from '../../components';
import FullLogo from '../../components/FullLogo';
import RegistrationForm from '../../components/RegistrationForm';
import SubmitCancelButtonGroup from '../../components/SubmitCancelButtonGroup';
import FadedText from '../../components/Text/FadedText';
import LinkText from '../../components/Text/LinkText';
import Toast from '../../components/Toast';
import { INTERNAL_LINKS } from '../../enum';
import i18n from '../../i18n';
import { updateSupportedCountries } from '../../stores/actions/common';
import { updateProfile } from '../../stores/actions/profile';
import { formatPageTitle } from '../../utils/common';
import { setAnalyticsUserProperties } from '../../utils/googleAnalytics';
import { PENDO_ANALYTICS } from '../../utils/pendoAnalytics';
import { hasUserRole } from '../../utils/roles';
import { SENTRY_ANALYTICS } from '../../utils/sentry';
import { selectStoreCountries, useStoreSelector } from '../../utils/storeSelectors';
import { normalizeUserSchema } from '../../utils/users';
import classNames from './styles.module.scss';

const UserAccountActivate = props => {
  const { t, ...rest } = props;

  const dispatch = useDispatch();
  const supportedCountries = useStoreSelector(selectStoreCountries, []);

  const [errorType, setErrorType] = useState(0);
  const [showError, setShowError] = useState(false);
  const [userToActivate, setUserToActivate] = useState();
  const [hasAddress, setHasAddress] = useState(true);

  const [showAddressStep, setShowAddressStep] = useState(false);
  const [activatedUser, setActivatedUser] = useState(null);
  const [userAddress, setUserAddress] = useState({});
  const [addressFormInstance] = Form.useForm();

  useQuery({
    enabled: showAddressStep && !supportedCountries?.length,
    queryKey: ['supported-countries'],
    queryFn: fetchCountryConfig,
    onSuccess: countries => dispatch(updateSupportedCountries(countries)),
    onError: () => {
      Toast({
        type: 'error',
        message: t('errorLoadingSupportedCountries'),
      });
    },
  });

  const tokenVerificationQuery = useQuery({
    queryKey: ['activate-account', props.match.params.token],
    queryFn: async () =>
      handleApiCalls(
        'get',
        `${process.env.REACT_APP_HOST_API}auth/activate-account/${props.match.params.token}`,
      ),
    onSuccess: response => {
      const data = get(response, 'data.data');
      setShowError(false);
      setHasAddress(!!get(data, 'homeAddress.streetOne'));
      setUserToActivate({
        _id: data._id,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        status: data.status,
        companyID: data.companyID,
      });
    },
    onError: err => {
      const IS_TOKEN_EXPIRED =
        err.response && err.response.data && /expired/gi.test(err.response.data);
      const IS_TOKEN_INVALID =
        err.response && err.response.data && /invalid/gi.test(err.response.data);
      const IS_ACTIVATED =
        err.response && err.response.data && /already activated/gi.test(err.response.data);

      if (err.response && (IS_TOKEN_EXPIRED || IS_TOKEN_INVALID)) {
        setErrorType(1);
      } else if (IS_ACTIVATED) {
        setErrorType(3);
      } else if (err.response && err.response.status === 404) {
        setErrorType(0);
      } else {
        setErrorType(0);
      }
      setShowError(true);
    },
  });

  const handleLogin = useCallback(
    async model => {
      try {
        let loginResult = await signInUser(model.email, model.password);

        if (loginResult.user) {
          setAnalyticsUserProperties(loginResult.user);
          PENDO_ANALYTICS.update(loginResult.user, loginResult.company);
          SENTRY_ANALYTICS.update(loginResult.user);

          const normalizedUser = normalizeUserSchema(loginResult.user);
          const language = normalizedUser?.preferences?.language || 'en';
          i18n.changeLanguage(language);

          dispatch(updateProfile(normalizedUser));
          return normalizedUser;
        }
      } catch (error) {
        handleApiErrors(error.response, () => {
          Toast({
            type: 'error',
            message: t('errorLoginIn'),
          });
        });
      }
    },
    [t, dispatch],
  );

  const activateAccountMutation = useMutation(
    data =>
      handleApiCalls(
        'post',
        `${process.env.REACT_APP_HOST_API}auth/activate-account/${props.match.params.token}`,
        data,
      ),
    {
      onSuccess: async () => {
        Toast({
          type: 'success',
          message: t('accountActivationSuccess'),
        });
        const loggedInUser = await handleLogin(userToActivate);
        if (hasUserRole(loggedInUser) && !hasAddress) {
          setActivatedUser(loggedInUser);
          setShowAddressStep(true);
        } else {
          setTimeout(() => {
            props.history.push(INTERNAL_LINKS.DASHBOARD);
          }, 1000);
        }
      },
      onError: err => {
        if (
          err.response &&
          err.response.status === 422 &&
          (err.response.data === 'The activation link is expired.' ||
            err.response.data === 'It looks like you clicked on an expired link')
        ) {
          setErrorType(1);
        } else if (
          err.response &&
          err.response.status === 422 &&
          err.response.data === 'This account has been already activated.'
        ) {
          setErrorType(3);
        } else if (err.response && err.response.status === 404) {
          setErrorType(0);
        } else {
          setErrorType(0);
          handleApiErrors(err.response);
        }
        setShowError(true);
      },
    },
  );

  const saveAddressMutation = useMutation(
    homeAddress => USER_API.updateUser(activatedUser._id, { personalInfor: { homeAddress } }),
    {
      onSuccess: () => {
        props.history.push(INTERNAL_LINKS.DASHBOARD);
      },
      onError: err => {
        handleApiErrors(err.response, () => {
          Toast({
            type: 'error',
            message: t('error'),
          });
        });
      },
    },
  );

  const resendEmail = useMutation(
    () =>
      handleApiCalls('post', `${process.env.REACT_APP_HOST_API}auth/resend-activate-email`, {
        token: props.match.params.token,
      }),
    {
      onSuccess: () => {
        Toast({
          type: 'success',
          message: t('activationEmailSendSuccessful'),
        });
      },
      onError: e => {
        handleApiErrors(e.response, () => {
          Toast({
            type: 'error',
            message: t('resendingEmailFailed'),
          });
        });
      },
    },
  );

  const handleActivate = useCallback(
    userData => {
      const newUserData = { ...userToActivate, ...userData };
      setUserToActivate(newUserData);
      activateAccountMutation.mutateAsync(newUserData);
    },
    [userToActivate, activateAccountMutation],
  );

  if (tokenVerificationQuery.isFetching || resendEmail.isLoading) {
    return (
      <>
        <Helmet>
          <title>{formatPageTitle('Activate Account')}</title>
        </Helmet>

        <Row justify="center" align={'middle'} className={classNames.activateView}>
          <div className="d-flex-center">
            <FadedText>{t('loading...')}</FadedText>
          </div>
        </Row>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{formatPageTitle('Activate Account')}</title>
      </Helmet>

      <Row
        justify="center"
        align={showError ? 'middle' : 'top'}
        className={classNames.activateView}
      >
        <Col flex={1}>
          <Row justify="center" style={{ marginBottom: 30 }}>
            <Col>
              <Link to={INTERNAL_LINKS.LOGIN}>
                <FullLogo />
              </Link>
            </Col>
          </Row>

          {showAddressStep && (
            <>
              <Row justify="center" style={{ marginBottom: 20 }}>
                <Col>
                  <FadedText>{t('updateHomeAddressTitle')}</FadedText>
                </Col>
              </Row>
              <AddressForm
                t={t}
                formInstance={addressFormInstance}
                countries={supportedCountries}
                values={userAddress}
                onValueChange={newValues =>
                  setUserAddress(current => ({ ...(current || {}), ...newValues }))
                }
              />
              <SubmitCancelButtonGroup
                hideCancel
                loading={saveAddressMutation.isLoading}
                onSubmit={() => {
                  addressFormInstance.validateFields().then(homeAddress => {
                    saveAddressMutation.mutateAsync(homeAddress);
                  });
                }}
              />
            </>
          )}

          {!showAddressStep &&
            tokenVerificationQuery.isFetched &&
            !resendEmail.isLoading &&
            !showError && (
              <RegistrationForm
                activating={activateAccountMutation.isLoading}
                user={userToActivate}
                fieldValues={handleActivate}
                t={t}
                props={rest}
              />
            )}

          {showError && errorType === 1 && (
            <div className="d-flex-center f-dir-column">
              <FadedText>
                <Trans
                  t={t}
                  i18nKey="emailVerificationLinkExpiredRequestNewHere"
                  components={[
                    <LinkText variant="strong" onClick={resendEmail.mutateAsync}>
                      dummy
                    </LinkText>,
                  ]}
                />
              </FadedText>
            </div>
          )}
          {showError && errorType === 3 && (
            <div className="d-flex-center f-dir-column">
              <FadedText>{t('accountAlreadyBeenActivated')}</FadedText>
              <br />
              <Link to={INTERNAL_LINKS.LOGIN}>
                <LinkText variant="strong">{t('loginToKliks')}</LinkText>
              </Link>
            </div>
          )}
          {showError && errorType === 2 && (
            <div className="d-flex-center">
              <FadedText>{t('emailVerificationLinkExpired')}</FadedText>
            </div>
          )}
          {showError && errorType === 0 && (
            <div className="d-flex-center">
              <FadedText>{t('unexpectedErrorContactAdmin')}</FadedText>
            </div>
          )}
        </Col>
      </Row>
    </>
  );
};

export default withNamespaces()(UserAccountActivate);
